import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import {
  ModalTemplateComponent,
  PencilModalSizeEnum,
} from 'src/app/standalones/components/modals/modal-template/modal-template.component';
import {
  PencilButtonComponent,
  PencilButtonThemeEnum,
} from 'src/app/standalones/components/pencil-button/pencil-button.component';
import { IconTypes } from 'src/app/standalones/components/pencil-icon/pencil-icon.component';
import { SVGIconComponentsModule } from 'src/app/common/svg-icon/svg-icon-components.module';
import { SlidesAccessApiService } from 'src/app/services/slides-import/slides-access.service';
import { SlidesAccess } from 'src/app/models/slides-import.models';

export interface SlidesImportAccessDialogData {
  presentationId: string;
}

@Component({
  selector: 'app-slides-import-access-dialog',
  templateUrl: './slides-import-access-dialog.component.html',
  styleUrls: ['./slides-import-access-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    ModalTemplateComponent,
    PencilButtonComponent,
    SVGIconComponentsModule,
  ],
})
export class SlidesImportAccessDialogComponent {
  readonly PencilModalSizeEnum = PencilModalSizeEnum;
  readonly PencilButtonThemeEnum = PencilButtonThemeEnum;
  readonly IconTypes = IconTypes;

  /** null = not yet checked, false = restricted */
  readonly accessState$ = new BehaviorSubject<boolean | null>(null);
  readonly isChecking$ = new BehaviorSubject(false);

  constructor(
    private readonly dialogRef: MatDialogRef<SlidesImportAccessDialogComponent>,
    private readonly slidesAccessService: SlidesAccessApiService,
    @Inject(MAT_DIALOG_DATA) private readonly data: SlidesImportAccessDialogData,
  ) {}

  onCancel(): void {
    this.dialogRef.close(false);
  }

  async onSlidesPublic(): Promise<void> {
    const access = await this.checkAccess();
    if (access === SlidesAccess.Public) {
      this.dialogRef.close(true);
    } else {
      this.accessState$.next(false);
    }
  }

  async onCheckAgain(): Promise<void> {
    const access = await this.checkAccess();
    if (access === SlidesAccess.Public) {
      this.dialogRef.close(true);
    } else {
      this.accessState$.next(false);
    }
  }

  onLogInInstead(): void {
    this.dialogRef.close(true);
  }

  private async checkAccess(): Promise<SlidesAccess> {
    this.isChecking$.next(true);
    try {
      return await firstValueFrom(this.slidesAccessService.checkAccess(this.data.presentationId));
    } finally {
      this.isChecking$.next(false);
    }
  }
}
